import React, { useState } from 'react';
import { Upload, Input, Button, Space, message, Image, Segmented } from 'antd';
import {
  UploadOutlined,
  LinkOutlined,
  DeleteOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import type { UploadProps } from 'antd';
import { uploadAPI } from '../services/api';

interface MediaUploaderProps {
  value?: string;
  onChange?: (value: string) => void;
  accept?: string;
  mediaType?: 'photo' | 'video';
}

const MediaUploader: React.FC<MediaUploaderProps> = ({
  value,
  onChange,
  accept,
  mediaType,
}) => {
  const [mode, setMode] = useState<'upload' | 'url'>(
    value && (value.startsWith('http://') || value.startsWith('https://')) ? 'url' : 'upload'
  );
  const [uploading, setUploading] = useState(false);
  const [uploadPercent, setUploadPercent] = useState(0);

  const acceptTypes = accept || (mediaType === 'video'
    ? 'video/mp4,video/quicktime,video/webm'
    : 'image/jpeg,image/png,image/gif,image/webp');

  const handleUpload: UploadProps['customRequest'] = async (options) => {
    const { file } = options;
    setUploading(true);
    setUploadPercent(0);

    try {
      const response = await uploadAPI.upload(file as File, (percent) => {
        setUploadPercent(percent);
      });
      const { url } = response.data;
      onChange?.(url);
      message.success('上传成功');
    } catch (err: any) {
      const errMsg = err.response?.data?.error || '上传失败';
      message.error(errMsg);
    } finally {
      setUploading(false);
      setUploadPercent(0);
    }
  };

  const handleClear = () => {
    onChange?.('');
  };

  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange?.(e.target.value);
  };

  // 完整 URL 直接用；相对路径（开发环境未配置 UPLOAD_BASE_URL）拼 API 地址用于预览
  const previewUrl = value ? (value.startsWith('http') ? value : uploadAPI.getFullUrl(value)) : '';
  const isImage = mediaType === 'photo' || (!mediaType && value && /\.(jpg|jpeg|png|gif|webp)(\?|$)/i.test(value));

  return (
    <div>
      <Segmented
        size="small"
        value={mode}
        onChange={(v) => setMode(v as 'upload' | 'url')}
        options={[
          { label: '上传文件', value: 'upload', icon: <UploadOutlined /> },
          { label: '输入URL', value: 'url', icon: <LinkOutlined /> },
        ]}
        style={{ marginBottom: 8 }}
      />

      {mode === 'upload' ? (
        <div>
          <Upload
            accept={acceptTypes}
            showUploadList={false}
            customRequest={handleUpload}
            disabled={uploading}
          >
            <Button
              icon={uploading ? <LoadingOutlined /> : <UploadOutlined />}
              loading={uploading}
              style={{ width: '100%' }}
            >
              {uploading ? `上传中 ${uploadPercent}%` : '选择文件上传'}
            </Button>
          </Upload>
        </div>
      ) : (
        <Input
          placeholder="输入图片/视频的 URL 地址"
          value={value}
          onChange={handleUrlChange}
          allowClear
        />
      )}

      {value && (
        <div style={{ marginTop: 8 }}>
          <Space direction="vertical" style={{ width: '100%' }} size={4}>
            {isImage && previewUrl && (
              <Image
                src={previewUrl}
                alt="预览"
                style={{ maxHeight: 120, objectFit: 'contain' }}
                fallback="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgZmlsbD0iI2YwZjBmMCIvPjx0ZXh0IHg9IjUwIiB5PSI1MCIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iIGZpbGw9IiM5OTkiIGZvbnQtc2l6ZT0iMTIiPuWKoOi9veWksei0pTwvdGV4dD48L3N2Zz4="
              />
            )}
            {!isImage && previewUrl && (
              <div style={{
                padding: '8px 12px',
                background: '#f5f5f5',
                borderRadius: 4,
                fontSize: 12,
                color: '#666',
                wordBreak: 'break-all',
              }}>
                {value}
              </div>
            )}
            <Button
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={handleClear}
            >
              清除
            </Button>
          </Space>
        </div>
      )}
    </div>
  );
};

export default MediaUploader;
